/**
 * ProductForm - product add-to-cart form; variant, quantity and submit handling.
 * Custom element: <product-form>
 *
 * Attributes (from $info):
 *   product                  Object   -
 *   default-variant          Object   -
 *   scroll-target-selector   String   null
 *   disable-default-variant  Boolean  false
 *   variant-quantities       Object   null
 *
 * Refs:
 *   [data-ref="options"]  (optional)
 *   [data-ref="quantitySelector"]  (required)
 *   [data-ref="productSalePrice"]  (optional)
 *   [data-ref="submitButton"]  (required)
 *   [data-ref="addToCartPrice"]  (optional)
 *   [data-ref="stickyBanner"]  (optional)
 *   [data-ref="form"]  (required)
 *   [data-ref="error"]  (required)
 *   [data-ref="errorMessage"]  (required)
 *
 * Loading: lazy-loaded by the theme loader (theme.js), which imports this
 * file by its exact name - the filename is load-bearing (import map in
 * snippets/theme-head.liquid).
 * Reconstructed to readable identifiers from the original minified vendor
 * bundle; behaviour verified unchanged by AST alpha-equivalence.
 */
var defineProperty = Object.defineProperty;
var setFunctionName = (fn, name) =>
  defineProperty(fn, "name", {
    value: name,
    configurable: !0,
  });
import "./component-QuantitySelector.js";
import "./component-ProductOptionPicker.js";
import "./component-ProductStickyBar.js";
import { ProductButtonOption } from "./component-ProductButtonOption.js";
import {
  ThemeComponent,
  setVariantUrlParam,
  formatMoney,
  cart,
  scrollToElement,
} from "./theme.js";
import { dist } from "./vendor.js";
import "./component-ProductDropdownOption.js";
import "./component-FakeDropdown.js";
import "./component-ProductForm.js";
class ProductForm extends ThemeComponent {
  constructor() {
    (super(...arguments),
      (this._selectedVariant = null),
      (this.quantity = 1),
      (this.siteFooter = document.querySelector(".site-footer")),
      (this._onSelectedPropertyChange = new dist.SimpleEventDispatcher()));
  }
  get selectedVariant() {
    return this._selectedVariant;
  }
  set selectedVariant(variant) {
    ((this._selectedVariant = variant), this.handleVariantChanged());
  }
  init() {
    ((this.selectedVariant = this.defaultVariant),
      this.listenForQuantityChange(),
      this.listenForVariantChange(),
      this.updateProductPrice(),
      this.listenForFormSubmission());
  }
  handleVariantChanged() {
    var currentVariant;
    // Card forms outside .product-main without the --card class (e.g. the split card style) set the page's variant param otherwise.
    this.classList.contains("product-form--card") ||
      !this.closest(".product-main") ||
      (this.closest(".template-product") &&
        setVariantUrlParam(
          ((currentVariant = this.selectedVariant) == null
            ? void 0
            : currentVariant.id) || null,
        ));
  }
  get onSelectedPropertyChange() {
    return this._onSelectedPropertyChange.asEvent();
  }
  checkVariantAvailability() {
    if (this.selectedVariant === null) {
      this.updateActionsVisibility(!0);
      return;
    }
    if (!this.selectedVariant.available) {
      this.updateActionsVisibility(!0);
      return;
    }
    this.updateActionsVisibility(!1);
  }
  listenForQuantityChange() {
    this.$subscribe(this.quantitySelector.onChange, (newQuantity) => {
      (this.error.classList.remove("is-active"),
        (this.quantity = newQuantity),
        this.updateProductPrice());
    });
  }
  listenForVariantChange() {
    this.options.forEach((optionPicker) => {
      optionPicker &&
        (this.disableDefaultVariant || optionPicker.renderOptions(),
        this.$subscribe(optionPicker.onSelectedVariantChange, (variant) => {
          var errorElement;
          if (variant !== null) {
            if (
              ((this.selectedVariant = variant),
              this.checkVariantAvailability(),
              this.variantQuantities &&
                this.variantQuantities[String(this.selectedVariant.id)])
            ) {
              const maxQuantity =
                String(
                  this.variantQuantities[String(this.selectedVariant.id)],
                ) || "9999";
              (this.quantitySelector.setMaxQuantity(maxQuantity),
                Number(this.quantitySelector.value) > Number(maxQuantity) &&
                  (this.quantitySelector.value = Number(maxQuantity)));
            }
            this.updateProductPrice(!1);
            for (const button of this.submitButton)
              (button.classList.remove(
                "product-form__actions__submit--disabled",
              ),
                (button.disabled = !1));
            (this._onSelectedPropertyChange.dispatch(variant),
              (errorElement = this.error) == null ||
                errorElement.classList.remove("is-active"));
          }
        }),
        (this.selectedVariant = optionPicker.selectedVariant));
    });
  }
  updateProductPrice(skipAddToCartPrice = !0) {
    if (this.selectedVariant) {
      const totalPrice = this.selectedVariant.price * this.quantity;
      let formattedPrice = formatMoney(totalPrice);
      if (typeof formattedPrice > "u") return;
      // Scoped to this form's own product-main: a document-wide query let any card form on the page overwrite the main price.
      const productMain = this.closest(".product-main");
      const priceElements = productMain
        ? Array.from(productMain.querySelectorAll(".js-product-price"))
        : [];
      for (const priceElement of priceElements)
        priceElement.textContent = formattedPrice;
      if (!skipAddToCartPrice) {
        formattedPrice = formatMoney(this.selectedVariant.price);
        for (const addToCartPriceElement of this.addToCartPrice)
          addToCartPriceElement.textContent = `${formattedPrice}`;
      }
      const salePriceElements = productMain
        ? Array.from(
            productMain.querySelectorAll('[data-ref="productSalePrice"]'),
          )
        : [];
      if (this.selectedVariant.compare_at_price === null)
        for (const salePriceElement of salePriceElements)
          salePriceElement.classList.add("visually-hidden");
      else {
        const compareAtTotal =
            this.selectedVariant.compare_at_price * this.quantity,
          formattedCompareAt = formatMoney(compareAtTotal);
        if (typeof formattedCompareAt > "u") return;
        for (const saleElement of salePriceElements)
          (saleElement.classList.remove("visually-hidden"),
            (saleElement.textContent = formattedCompareAt));
      }
    }
  }
  updateStickyBannerProductOptions() {
    if (
      !this.stickyBanner.stickyBannerProductOptions ||
      this.selectedVariant === null
    )
      return;
    let optionIndex = 1;
    for (const optionValue of this.selectedVariant.options) {
      optionIndex == 1 &&
        this.stickyBanner.options &&
        ((this.stickyBanner.options.option1.selected = optionValue),
        (this.options[0].option1.selected = optionValue),
        this.stickyBanner.options.option1 instanceof ProductButtonOption &&
          this.stickyBanner.options.option1.updateButtonStyles(),
        this.options[0].option1 instanceof ProductButtonOption &&
          this.options[0].option1.updateButtonStyles());
      const optionDataElement =
        this.stickyBanner.stickyBannerProductOptions.querySelector(
          `.product-sticky__details__options__option:nth-child(${optionIndex}) .product-sticky__details__options__option__data`,
        );
      if (optionDataElement == null) return;
      ((optionDataElement.textContent = optionValue), optionIndex++);
    }
  }
  listenForFormSubmission() {
    for (const button of this.submitButton)
      this.$listen(button, "click", () => {
        this.addToCart().catch((error) => {
          this.$log.error("Add to cart failed", {
            e: error,
          });
        });
      });
  }
  updateActionsVisibility(isUnavailable) {
    if (isUnavailable)
      for (const button of this.submitButton)
        button.classList.add("product-form__actions__submit--unavailable");
    else
      for (const button of this.submitButton)
        button.classList.remove("product-form__actions__submit--unavailable");
  }
  async addToCart() {
    const properties = this.getFormData();
    try {
      const variant = this.selectedVariant;
      if (!variant) {
        this.showError("Please select an option.");
        return;
      }
      (await cart.asInteraction(async () => {
        await cart.addItem({
          id: variant.id,
          quantity: this.quantity,
          properties: properties,
        });
      }),
        scrollToElement(this));
    } catch (error) {
      this.$log.error("Cart error", {
        event: error,
      });
      const { message: message } = error;
      this.showError(message);
    }
  }
  getFormData() {
    const formData = new FormData(this.form),
      properties = {};
    for (const [key, value] of formData.entries())
      if (key.indexOf("properties") > -1) {
        const propertyName = key.replace("properties[", "").replace("]", "");
        properties[propertyName] = value;
      }
    return properties;
  }
  showError(message) {
    ((this.errorMessage.textContent = message),
      message
        ? this.error.classList.add("is-active")
        : this.error.classList.remove("is-active"));
  }
  addStickyBannerEventListeners() {
    this.scrollTargetSelector &&
      ((this.scrollTargetElement = document.querySelector(
        this.scrollTargetSelector,
      )),
      !(!this.stickyBanner || !this.scrollTargetElement) &&
        (this.observeScroll(), this.listenForEditButtonClick()));
  }
  observeScroll() {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries, _observer) => {
        entries[0].isIntersecting
          ? this.stickyBanner.classList.add("product-sticky--hidden")
          : this.stickyBanner.classList.remove("product-sticky--hidden");
      }, observerOptions);
      (this.scrollTargetElement && observer.observe(this.scrollTargetElement),
        this.siteFooter && observer.observe(this.siteFooter));
    }
  }
  listenForEditButtonClick() {
    this.stickyBanner.editButton &&
      this.$listen(this.stickyBanner.editButton, "click", () => {
        scrollToElement(this);
      });
  }
}
setFunctionName(ProductForm, "ProductForm");
const propertyTypeConstructors = {
  Object,
  String,
  Boolean,
};
ProductForm.prototype.$name = "ProductForm";
ProductForm.prototype.$info = JSON.parse(
  '{"name":"ProductForm","meta":{"isLegacy":false,"selector":"product-form"},"props":[{"attr":"product","reflectAttr":false,"propertyKey":"product","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"Shopify.Liquid.Product"},{"attr":"default-variant","reflectAttr":false,"propertyKey":"defaultVariant","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"Shopify.Liquid.Variant"},{"attr":"scroll-target-selector","default":null,"reflectAttr":false,"propertyKey":"scrollTargetSelector","propertyType":"String","propertyTypeName":"String","propertyTsTypeName":"string|null"},{"attr":"disable-default-variant","default":false,"reflectAttr":false,"propertyKey":"disableDefaultVariant","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"variant-quantities","default":null,"reflectAttr":false,"propertyKey":"variantQuantities","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"VariantQuantities|null"}],"refs":[{"selector":"[data-ref=\\"options\\"]","required":false,"propertyKey":"options","propertyType":"Array","propertyTypeName":"Array","propertyTsTypeName":"ProductOptionPicker[]"},{"selector":"[data-ref=\\"quantitySelector\\"]","required":true,"propertyKey":"quantitySelector","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"QuantitySelector"},{"selector":"[data-ref=\\"productSalePrice\\"]","required":false,"propertyKey":"productSalePrice","propertyType":"Array","propertyTypeName":"Array","propertyTsTypeName":"HTMLElement[]"},{"selector":"[data-ref=\\"submitButton\\"]","required":true,"propertyKey":"submitButton","propertyType":"Array","propertyTypeName":"Array","propertyTsTypeName":"HTMLButtonElement[]"},{"selector":"[data-ref=\\"addToCartPrice\\"]","required":false,"propertyKey":"addToCartPrice","propertyType":"Array","propertyTypeName":"Array","propertyTsTypeName":"HTMLElement[]"},{"selector":"[data-ref=\\"stickyBanner\\"]","required":false,"propertyKey":"stickyBanner","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"ProductStickyBar"},{"selector":"[data-ref=\\"form\\"]","required":true,"propertyKey":"form","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLFormElement"},{"selector":"[data-ref=\\"error\\"]","required":true,"propertyKey":"error","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLElement"},{"selector":"[data-ref=\\"errorMessage\\"]","required":true,"propertyKey":"errorMessage","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLElement"}]}',
);
ProductForm.prototype.$info.component = ProductForm;
for (const prop of ProductForm.prototype.$info.props)
  prop.propertyType = propertyTypeConstructors[prop.propertyType];
window.componentService.registerComponent(
  "Product/ProductForm.ts",
  {
    ProductForm: ProductForm,
  },
  void 0,
);
